'use client';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserType } from '@/@types';
import { API } from '@/services/api';
import { useEvent } from './events';

type UserContextType<TLogged extends Boolean = false> = {
  isLogged: boolean;
  api: ReturnType<typeof API>;
  emitter?: ReturnType<typeof useEvent>;
  isBot: boolean;
} & (TLogged extends true ? { user: UserType; token: string } : { user?: UserType; token?: string });

const Context = React.createContext<UserContextType>({
  isLogged: false,
  isBot: false,
  api: API(),
});

export const UserContext: React.FC<
  React.PropsWithChildren & {
    user: UserType;
    token: string;
    isBot: boolean;
    geohash?: string;
    locale?: string;
  }
> = ({ children, user: userProp, token, isBot, geohash, locale }) => {
  const emitter = useEvent();
  const [user, setUser] = useState(userProp);
  useEffect(() => {
    const update = (updated: CustomEvent<UserType>) => {
      setUser(updated.detail);
    };
    emitter.addEventListener('user-update', update);
    return () => {
      emitter.removeEventListener('user-update', update);
    };
  }, [emitter, user]);
  const value = useMemo(() => {
    return { user, token, api: API({ token, user, emitter, geohash, locale }), emitter, isLogged: !!token, isBot };
  }, [user, token, emitter, isBot, geohash, locale]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useUser = <TLogged extends Boolean = false>(): UserContextType<TLogged> => {
  return useContext(Context) as any;
};
